import React from 'react'
import ReactDOM from 'react-dom/client'
import { onAuthStateChanged } from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { auth, db, functions } from './lib/firebase'
import { initializePush } from './lib/messaging'
import useStore from './store/useStore'
// Side-effect import: applies stored theme to <html> before React paints.
import './lib/theme'
import App from './App'
import './index.css'

onAuthStateChanged(auth, async firebaseUser => {
  const { setUser, setLoading } = useStore.getState()

  if (firebaseUser) {
    try {
      const snap = await getDoc(doc(db, 'users', firebaseUser.uid))
      const data = snap.exists() ? snap.data() : {}
      setUser({
        uid:                firebaseUser.uid,
        email:              firebaseUser.email,
        displayName:        data.displayName ?? firebaseUser.displayName,
        photoURL:           firebaseUser.photoURL,
        role:               data.role ?? 'customer',
        nurseryName:        data.nurseryName,
        phone:              data.phone,
        pinCode:            data.pinCode,
        building:           data.building,
        locality:           data.locality,
        city:               data.city,
        stateName:          data.stateName,
        gstNumber:          data.gstNumber,
        panNumber:          data.panNumber,
        gstUnregisteredDeclaredAt:         data.gstUnregisteredDeclaredAt,
        gstUnregisteredDeclarationVersion: data.gstUnregisteredDeclarationVersion,
        billingAddress:     data.billingAddress,
        billingCity:        data.billingCity,
        billingState:       data.billingState,
        billingPin:         data.billingPin,
        bankAccount:        data.bankAccount,
        ifscCode:           data.ifscCode,
        supplierId:         data.supplierId,
        pushOptOut:         data.pushOptOut         ?? false,
        emailNotifications: data.emailNotifications ?? true,
      })
      // Silent re-registration of FCM token for the supplier. Skipped if
      // they've explicitly muted push from Account → Notifications.
      if (data.role === 'supplier' && !data.pushOptOut) {
        initializePush(firebaseUser.uid)
      }

      // Storage rules check the Auth token's `role` custom claim, not the
      // Firestore doc — older supplier accounts never had the claim set
      // (the promote function used to skip it), so /plants/** uploads
      // failed with storage/unauthorized. Mirror Firestore → Auth claim
      // once per session and force an ID-token refresh so the upload
      // works on this very session.
      const tokenResult = await firebaseUser.getIdTokenResult()
      if (tokenResult.claims.role !== data.role && data.role) {
        try {
          await httpsCallable(functions, 'selfSyncRoleClaim')()
          await firebaseUser.getIdToken(true)   // force refresh
        } catch (err) {
          console.warn('[supplier] selfSyncRoleClaim failed', err)
        }
      }
    } catch {
      setUser({
        uid:         firebaseUser.uid,
        email:       firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL:    firebaseUser.photoURL,
        role:        'customer',
      })
    }
  } else {
    setUser(null)
  }

  setLoading(false)

  if (!document.getElementById('root')!.hasChildNodes()) {
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode><App /></React.StrictMode>
    )
  }
})
